import { Images } from '../assets'
import React from 'react'
import { Link } from 'react-router-dom'
import PageTitle from '../components/PageTitle'
import Tag from '../components/Tag'

const PDFViewer = ({ src, title }) => {
  const [scale, setScale] = React.useState(1)
  const containerRef = React.useRef(null)

  const zoomIn = () => setScale((s) => Math.min(s + 0.25, 3))
  const zoomOut = () => setScale((s) => Math.max(s - 0.25, 0.5))
  const resetZoom = () => setScale(1)

  const toggleFullscreen = () => {
    const el = containerRef.current
    if (!el) return
    if (document.fullscreenElement) {
      document.exitFullscreen()
    } else {
      el.requestFullscreen()
    }
  }

  return (
    <div
      ref={containerRef}
      className="flex flex-col rounded-2xl overflow-hidden border border-[#E6E6E6] bg-[#F8F9FB] shadow-sm"
    >
      <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 bg-white border-b border-[#E6E6E6]">
        <span className="text-sm font-medium text-[#2A2A2A]">{title}</span>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={zoomOut}
            className="w-8 h-8 rounded-lg bg-[#F3F8FF] text-[#007DD6] hover:bg-[#E8F0FE] flex items-center justify-center transition-colors"
            aria-label="Zoom out"
            title="Zoom out"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="5" y1="12" x2="19" y2="12" /></svg>
          </button>
          <button
            type="button"
            onClick={resetZoom}
            className="text-xs font-medium px-2 h-8 rounded-lg bg-[#F3F8FF] text-[#007DD6] hover:bg-[#E8F0FE] transition-colors min-w-[48px]"
            aria-label="Reset zoom"
            title="Reset zoom"
          >
            {Math.round(scale * 100)}%
          </button>
          <button
            type="button"
            onClick={zoomIn}
            className="w-8 h-8 rounded-lg bg-[#F3F8FF] text-[#007DD6] hover:bg-[#E8F0FE] flex items-center justify-center transition-colors"
            aria-label="Zoom in"
            title="Zoom in"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></svg>
          </button>
          <div className="w-px h-6 bg-[#E6E6E6] mx-1" />
          <button
            type="button"
            onClick={toggleFullscreen}
            className="w-8 h-8 rounded-lg bg-[#F3F8FF] text-[#007DD6] hover:bg-[#E8F0FE] flex items-center justify-center transition-colors"
            aria-label="Fullscreen"
            title="Fullscreen"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M8 3H5a2 2 0 0 0-2 2v3m18 0V5a2 2 0 0 0-2-2h-3m0 18h3a2 2 0 0 0 2-2v-3M3 16v3a2 2 0 0 0 2 2h3" /></svg>
          </button>
          <a
            href={src}
            download
            className="w-8 h-8 rounded-lg bg-[#F3F8FF] text-[#007DD6] hover:bg-[#E8F0FE] flex items-center justify-center transition-colors"
            aria-label="Download PDF"
            title="Download PDF"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="7 10 12 15 17 10" /><line x1="12" y1="15" x2="12" y2="3" /></svg>
          </a>
          <a
            href={src}
            target="_blank"
            rel="noopener noreferrer"
            className="w-8 h-8 rounded-lg bg-[#F3F8FF] text-[#007DD6] hover:bg-[#E8F0FE] flex items-center justify-center transition-colors"
            aria-label="Open in new tab"
            title="Open in new tab"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" /><polyline points="15 3 21 3 21 9" /><line x1="10" y1="14" x2="21" y2="3" /></svg>
          </a>
        </div>
      </div>
      <div className="relative w-full aspect-[3/4] overflow-auto bg-[#E8E8E8]">
        <iframe
          src={`${src}#toolbar=1&navpanes=0`}
          title={title}
          className="w-full h-full origin-top-left"
          style={{ transform: `scale(${scale})`, width: `${100 / scale}%`, height: `${100 / scale}%` }}
          loading="lazy"
        />
      </div>
    </div>
  )
}

const posters = [
  {
    number: 'Poster 1 of 2',
    title: 'Patient-Centered Quality Improvement: Implementing Digital Feedback Systems in Nigerian HIV Care Settings',
    body: `This study examined how the Clientshot platform enables structured patient feedback collection in Nigerian HIV programme settings - helping facilities identify service gaps and implement targeted quality improvement actions. The findings drew on deployment data from multiple states and demonstrated the platform's effectiveness in generating actionable insights for healthcare providers.`
  },
  {
    number: 'Poster 2 of 2',
    title: 'Improving Healthcare Experiences: Evaluating the Perception of Healthcare Workers in Selected Nigerian States',
    body: 'This work explored healthcare worker perspectives on digital patient feedback systems across selected Nigerian states, demonstrating how structured feedback tools can support more responsive and data-driven care delivery. The findings highlighted the role of healthcare provider buy-in in the successful adoption of digital quality improvement tools.'
  }
]

const highlights = [
  'Real-world implementation evidence from Nigerian HIV programme settings shared at a leading global scientific conference',
  'Nigerian-led digital health solutions brought into global scientific discussions on HIV care innovation',
  'Direct engagement with researchers, implementers, and policymakers working on HIV care outcomes worldwide',
  'Peer-reviewed evidence demonstrating that digital feedback systems improve patient experience and quality of care in resource-limited settings'
]

const Publications = () => {
  React.useEffect(() => {
    window.scrollTo(0, 0)
  }, [])

  return (
    <>
      <PageTitle title="Publications and Research | SeamHealth Innovations" />

      {/* Hero */}
      <section className="relative py-28 md:py-44 px-8 md:px-12 overflow-hidden">
        <video
          className="absolute inset-0 w-full h-full object-cover z-[1]"
          autoPlay
          loop
          muted
          playsInline
          controls={false}
          disablePictureInPicture
        >
          <source src={Images.ProjectVid} type="video/mp4" />
        </video>
        <div className="absolute inset-0 z-[2] bg-gradient-to-r from-[#002E5C]/80 to-[#145684]/80" />
        <div className="max-w-[1280px] mx-auto relative z-10">
          <div className="max-w-[800px]">
            <nav data-aos="fade-up" aria-label="Breadcrumb" className="mb-4">
              <ol className="flex items-center gap-2 text-sm text-white/70">
                <li>
                  <Link to="/" className="hover:text-white transition-colors">Home</Link>
                </li>
                <li aria-hidden="true">/</li>
                <li className="text-white" aria-current="page">Publications</li>
              </ol>
            </nav>
            <h1 data-aos="fade-up" className="text-5xl font-bold text-white mb-6">
              Publications and Events
            </h1>
            <p data-aos="fade-up" className="text-lg text-white/70 leading-relaxed mb-4">
              Research, evidence, and engagement from the SeamHealth team.
            </p>
            <p data-aos="fade-up" className="text-white/50 leading-relaxed">
              We do not just build and deploy - we contribute to the evidence base. Our team participates in global health conferences, produces implementation evidence from our programme deployments, and shares findings with the wider digital health community. This page is a record of that work.
            </p>
          </div>
        </div>
        <div className="absolute bottom-0 left-0 right-0 z-10 h-32 md:h-44">
          <svg viewBox="0 0 1440 200" fill="none" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="none" className="w-full h-full block">
            <path d="M0 200H1440V0L0 200Z" fill="white" />
          </svg>
        </div>
      </section>

      {/* INTEREST 2025 Conference */}
      <section className="max-w-[1280px] mx-auto px-8 md:px-12 py-36 md:py-44">
        <div className="grid md:grid-cols-2 gap-16 items-center">
          <div data-aos="fade-up">
            <div className="mb-4">
              <Tag title="19th International Conference on HIV Treatment, Pathogenesis, and Prevention Research" />
            </div>
            <h1 className="text-5xl font-bold text-[#2A2A2A] mb-4">INTEREST 2025 - Windhoek, Namibia</h1>
            <p className="text-sm text-[#7E7E7E] mb-6">13 to 16 May 2025</p>
            <p className="text-[#5B5B5B] leading-relaxed mb-4">
              INTEREST is one of the leading global scientific conferences on HIV research, bringing together researchers, implementers, policymakers, and technology innovators working to improve HIV care outcomes worldwide. In May 2025, our team travelled to Windhoek, Namibia, to present findings from our Clientshot deployments in Nigerian HIV care settings.
            </p>
            <p className="text-[#5B5B5B] leading-relaxed">
              We presented two peer-reviewed scientific posters, sharing implementation evidence on how digital feedback systems can strengthen patient-centred care and continuous quality improvement in HIV service delivery. Our participation reflected SeamHealth's commitment to grounding our technology work in evidence - and to contributing that evidence to global scientific discussions rather than keeping it internal.
            </p>
          </div>
          <div data-aos="fade-left" className="group rounded-2xl overflow-hidden aspect-[4/3] bg-gray-100 border border-[#E6E6E6]">
            <img decoding="async" src={Images.InterestConference} alt="INTEREST 2025 conference hall in Windhoek, Namibia" className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" loading="lazy" />
          </div>
        </div>
      </section>

      {/* Poster Presentations */}
      <section className="bg-[#F8F9FB] py-36 md:py-44">
        <div className="max-w-[1280px] mx-auto px-8 md:px-12">
          <h1 data-aos="fade-up" className="text-5xl font-bold text-[#2A2A2A] mb-4">Poster Presentations</h1>
          <div className="grid md:grid-cols-2 gap-8 mt-12">
            {posters.map((pub, i) => (
              <div key={i} data-aos="fade-up" data-aos-delay={i * 100} className="rounded-xl border border-[#E6E6E6] bg-white overflow-hidden flex flex-col">
                <div className="group overflow-hidden aspect-[16/10] bg-gray-100">
                  <img
                    src={i === 0 ? Images.InterestTeamPoster : Images.InterestDelegates}
                    alt={i === 0 ? 'SeamHealth team presenting research poster at INTEREST 2025' : 'SeamHealth delegates at INTEREST 2025'}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                    loading="lazy"
                  />
                </div>
                <div className="p-8 flex flex-col flex-1">
                  <span className="text-sm text-[#007DD6] font-medium mb-3">{pub.number}</span>
                  <h5 className="text-lg font-semibold text-[#2A2A2A] mb-4 leading-snug">{pub.title}</h5>
                  <p className="text-sm text-[#5B5B5B] leading-relaxed flex-1">{pub.body}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Key Highlights */}
      <section className="max-w-[1280px] mx-auto px-8 md:px-12 py-36 md:py-44">
        <h1 data-aos="fade-up" className="text-5xl font-bold text-[#2A2A2A] mb-16">What our INTEREST 2025 participation represented</h1>
        <div className="grid md:grid-cols-2 gap-x-16 gap-y-10">
          {highlights.map((point, i) => (
            <div key={i} data-aos="fade-up" className="flex gap-4">
              <div className="w-6 h-6 shrink-0 mt-0.5">
                <svg viewBox="0 0 24 24" fill="none" className="w-full h-full text-[#007DD6]">
                  <path d="M20 6L9 17l-5-5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </div>
              <p className="text-[#2A2A2A] leading-relaxed">{point}</p>
            </div>
          ))}
        </div>
      </section>

      {/* INTEREST 2025 Gallery */}
      <section className="max-w-[1280px] mx-auto px-8 md:px-12 pb-36 md:pb-44 -mt-20 md:-mt-28">
        <div data-aos="fade-up" className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="group relative rounded-2xl overflow-hidden bg-gray-100 border border-[#E6E6E6] aspect-[3/4] md:aspect-auto md:row-span-2">
            <img
              src={Images.InterestSpeaker}
              alt="SeamHealth representative at INTEREST 2025"
              className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
              loading="lazy"
            />
          </div>
          <div className="group relative rounded-2xl overflow-hidden bg-gray-100 border border-[#E6E6E6] aspect-[4/3]">
            <img
              src={Images.InterestTeamSelfie}
              alt="SeamHealth team members at INTEREST 2025"
              className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
              loading="lazy"
            />
          </div>
          <div className="group relative rounded-2xl overflow-hidden bg-gray-100 border border-[#E6E6E6] aspect-[4/3]">
            <img
              src={Images.InterestBackdrop}
              alt="INTEREST 2025 conference backdrop"
              className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
              loading="lazy"
            />
          </div>
        </div>
      </section>

      {/* Read the Publication */}
      <section className="bg-[#F8F9FB] py-36 md:py-44">
        <div className="max-w-[1280px] mx-auto px-8 md:px-12">
          <div className="text-center max-w-[800px] mx-auto mb-16">
            <h1 data-aos="fade-up" className="text-5xl font-bold text-[#2A2A2A] mb-6">Read the Publication</h1>
            <p data-aos="fade-up" className="text-[#5B5B5B] leading-relaxed">
              Explore the complete INTEREST 2025 conference publication below. This paper presents SeamHealth's implementation experience, key findings, and practical insights from deploying digital health solutions that strengthen healthcare systems across Africa.
            </p>
            <p data-aos="fade-up" className="text-[#5B5B5B] leading-relaxed mt-4">
              Use the embedded document to read the publication directly, or download a copy for offline reference.
            </p>
          </div>
          <div data-aos="fade-up" className="grid md:grid-cols-2 gap-8">
            <PDFViewer src={Images.InterestAbstract346} title="INTEREST 2025 Abstract 346" />
            <PDFViewer src={Images.InterestAbstract987} title="INTEREST 2025 Abstract 987" />
          </div>
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="relative bg-gradient-to-br from-[#001630] to-[#002A4D] overflow-hidden">
        <video autoPlay loop muted playsInline className="absolute inset-0 w-full h-full object-cover z-[1] opacity-[0.2]">
          <source src={Images.FuturisticBackground} type="video/mp4" />
        </video>
        <div className="absolute inset-0 z-[2] bg-gradient-to-br from-[#001630]/60 to-[#002A4D]/60" />
        <div className="relative z-10 max-w-[1280px] mx-auto px-8 md:px-12">
          <div className="grid gap-12 items-stretch min-h-[420px] md:min-h-[520px]">
            <div className="flex flex-col justify-center py-12 md:py-20">
              <h1 data-aos="fade-up" className="text-5xl text-white tracking-tight">
                Want to collaborate on research?
              </h1>
              <p data-aos="fade-up" className="text-lg text-white/70 mt-4 leading-relaxed max-w-[480px]">
                If you are working on digital health research, implementation science, or programme evaluation and would like to explore a collaboration, we would like to hear from you.
              </p>
              <div data-aos="fade-up" className="mt-8">
                <Link to="/get-in-touch" className="btn btn-primary text-base w-fit">
                  Get in touch <img decoding="async" src={Images.ArrowUpRight} alt="Arrow icon" loading="lazy" />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  )
}

export default Publications
